import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { TractorServiceStatus } from "@/lib/tractor-service-status";
import { isBlockingOosStatus } from "@/lib/tractor-service-status";
import {
  ELIGIBILITY_ERROR_LABEL,
  ELIGIBILITY_LOADING_LABEL,
  ELIGIBILITY_MISSING_LABEL,
  type EligibilityBlockReason,
  type EligibilityGateState,
} from "@/lib/truck-eligibility";

interface TractorOption {
  id: string;
  name: string;
}

interface TractorComboboxProps {
  tractors: TractorOption[] | undefined;
  /** Tractor id, or "" / "none" for unassigned. */
  value: string;
  /** Called with the tractor id, or "none" for unassigned. */
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  /** Tractor ids that should render disabled (e.g. scheduling conflicts). */
  disabledIds?: Set<string>;
  /** Per-tractor truck-eligibility block reason for the selected trip type.
   *  Computed by `computeEligibilityReasons`. `missing`/`unknown` render as
   *  "Eligibility Data Missing"; `mode` renders as "not eligible". */
  eligibilityReasons?: Map<string, EligibilityBlockReason>;
  /** Eligibility feature state. While `loading`/`error` every tractor is
   *  temporarily blocked, but never labelled "Eligibility Data Missing". */
  eligibilityGateState?: EligibilityGateState;
  /** Optional per-tractor OOS status. When provided, blocking statuses render
   *  disabled with an "OOS" label; `upcoming_service` renders enabled with a
   *  subtle badge. Populated by `useTractorOosStatusMap`. */
  oosStatusById?: Map<string, TractorServiceStatus>;
}


export function TractorCombobox({
  tractors,
  value,
  onChange,
  disabled,
  placeholder = "Select tractor",
  id,
  disabledIds,
  eligibilityReasons,
  eligibilityGateState = "off",
  oosStatusById,
}: TractorComboboxProps) {
  const [open, setOpen] = useState(false);
  const normalized = value && value !== "none" ? value : "";
  const selected = tractors?.find((t) => t.id === normalized);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
          )}
        >
          {selected ? selected.name : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder="Search tractors..." />
          <CommandList className="max-h-[260px] overflow-y-auto">
            <CommandEmpty>No tractors found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => {
                  onChange("none");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !normalized ? "opacity-100" : "opacity-0",
                  )}
                />
                None
              </CommandItem>
              {tractors?.map((t) => {
                // Blocking is driven exclusively by `disabledIds`, which the
                // parent computes with window-aware OOS + booking conflicts.
                // `oosStatusById` reflects the tractor's status *right now*
                // and must only influence labeling, never disable state —
                // otherwise a currently-OOS truck whose service window ends
                // before the requested window would be wrongly blocked.
                const eligibilityReason = eligibilityReasons?.get(t.id);
                const gatePending =
                  eligibilityGateState === "loading" ||
                  eligibilityGateState === "error";
                const isIneligible = !!eligibilityReason || gatePending;
                const isBlocked = !!disabledIds?.has(t.id) || isIneligible;
                const oosStatus = oosStatusById?.get(t.id);
                const isOosBlockingNow =
                  !!oosStatus && isBlockingOosStatus(oosStatus);
                const isUpcoming = oosStatus === "upcoming_service";
                // Labeling priority: OOS wins over eligibility, since an
                // out-of-service truck is the more actionable reason and many
                // OOS trucks also lack an AAM eligibility record.
                const badgeLabel = isOosBlockingNow
                  ? "OOS"
                  : gatePending
                    ? eligibilityGateState === "loading"
                      ? ELIGIBILITY_LOADING_LABEL
                      : ELIGIBILITY_ERROR_LABEL
                    : eligibilityReason === "missing" || eligibilityReason === "unknown"
                      ? ELIGIBILITY_MISSING_LABEL
                      : eligibilityReason === "mode"
                        ? "not eligible"
                        : isBlocked
                          ? "booked"
                          : isUpcoming
                            ? "upcoming service"
                            : null;


                return (
                  <CommandItem
                    key={t.id}
                    value={t.name}
                    disabled={isBlocked}
                    aria-disabled={isBlocked}
                    className={cn(
                      isBlocked && "pointer-events-none cursor-not-allowed bg-muted text-muted-foreground opacity-60 data-[selected=true]:bg-muted data-[selected=true]:text-muted-foreground",
                    )}
                    onSelect={() => {
                      if (isBlocked) return;
                      onChange(t.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        normalized === t.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="flex-1">{t.name}</span>
                    {badgeLabel && (
                      <span
                        className={cn(
                          "ml-2 text-xs",
                          isOosBlockingNow || isIneligible
                            ? "text-destructive"
                            : "text-muted-foreground",
                        )}
                      >
                        {badgeLabel}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
